import React, { useState, useEffect, useRef } from 'react';
import mqtt from 'mqtt';

const MqttConsole = () => {
  const [messages, setMessages] = useState([]);
  const [subscribedTopics, setSubscribedTopics] = useState([]);
  const [topicToSubscribe, setTopicToSubscribe] = useState('devices/+/telemetry');
  const [topicToPublish, setTopicToPublish] = useState('devices/arduino_001/command');
  const [messageToPublish, setMessageToPublish] = useState('');
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [showJsonFormatter, setShowJsonFormatter] = useState(false);
  const clientRef = useRef(null);

  // Cargar suscripciones y mensajes guardados al iniciar
  useEffect(() => {
    // Cargar suscripciones
    const savedSubscriptions = localStorage.getItem('mqttSubscriptions');
    if (savedSubscriptions) {
      try {
        const saved = JSON.parse(savedSubscriptions);
        if (Array.isArray(saved) && saved.length > 0) {
          console.log('Loading saved subscriptions:', saved);
        }
      } catch (e) {
        console.error('Error loading saved subscriptions:', e);
      }
    }

    // Cargar log de mensajes persistente
    const savedMessages = localStorage.getItem('mqttMessageLog');
    if (savedMessages) {
      try {
        const saved = JSON.parse(savedMessages);
        if (Array.isArray(saved) && saved.length > 0) {
          setMessages(saved);
          console.log(`Loaded ${saved.length} messages from persistent log`);
        }
      } catch (e) {
        console.error('Error loading saved messages:', e);
      }
    }
    
    connectToBroker();
    
    return () => {
      if (clientRef.current) {
        clientRef.current.end();
      }
    };
  }, []);

  // Guardar suscripciones cuando cambien
  useEffect(() => {
    if (subscribedTopics.length > 0) {
      localStorage.setItem('mqttSubscriptions', JSON.stringify(subscribedTopics));
    }
  }, [subscribedTopics]);

  const connectToBroker = () => {
    setConnecting(true);

    // Conectar via WebSocket al broker MQTT a través de Nginx proxy
    // Usar WSS si la página se carga via HTTPS, WS si es HTTP
    const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
    const brokerUrl = `${protocol}nubemotic.cloud/mqtt/`;
    const options = {
      clientId: 'mqtt_console_' + Math.random().toString(16).substr(2, 8),
      clean: true,
      connectTimeout: 4000,
      reconnectPeriod: 1000,
      username: 'devices',
      password: 'bfy90rmi2025divices',
    };

    const client = mqtt.connect(brokerUrl, options);
    clientRef.current = client;

    client.on('connect', () => {
      console.log('Connected to MQTT broker');
      setConnected(true);
      setConnecting(false);
      addMessage('system', 'Connected to MQTT broker', '✅ CONNECTED');
      
      // Re-suscribirse a los topics guardados
      const savedSubscriptions = localStorage.getItem('mqttSubscriptions');
      if (savedSubscriptions) {
        try {
          const saved = JSON.parse(savedSubscriptions);
          saved.forEach(topic => {
            client.subscribe(topic, (err) => {
              if (!err) {
                addMessage('system', topic, '🔄 AUTO-SUBSCRIBED (from saved)');
              }
            });
          });
          setSubscribedTopics(saved);
        } catch (e) {
          console.error('Error restoring subscriptions:', e);
        }
      }
    });

    client.on('message', (topic, message) => {
      const payload = message.toString();
      
      // Intentar parsear como JSON para mejor visualización
      let formattedPayload = payload;
      try {
        const jsonData = JSON.parse(payload);
        formattedPayload = JSON.stringify(jsonData, null, 2);
      } catch (e) {
        // No es JSON, mostrar como texto plano
      }
      
      addMessage('received', topic, formattedPayload, payload);
    });

    client.on('error', (error) => {
      console.error('MQTT error:', error);
      addMessage('error', 'Connection error', error.message);
      setConnecting(false);
    });

    client.on('offline', () => {
      setConnected(false);
      addMessage('system', 'Disconnected', '⚠️ OFFLINE');
    });

    client.on('reconnect', () => {
      addMessage('system', 'Reconnecting...', '🔄 RECONNECTING');
    });
  };

  const addMessage = (type, topic, payload, rawPayload = null) => {
    const newMessage = {
      id: Date.now() + Math.random(),
      type,
      topic,
      payload,
      rawPayload: rawPayload || payload,
      timestamp: new Date().toLocaleTimeString(),
      date: new Date().toLocaleDateString()
    };
    setMessages(prev => {
      const updated = [newMessage, ...prev].slice(0, 100); // Mantener últimos 100 mensajes
      // Guardar en localStorage para persistencia
      try {
        localStorage.setItem('mqttMessageLog', JSON.stringify(updated));
      } catch (e) {
        console.warn('Could not save messages to localStorage:', e);
      }
      return updated;
    });
  };

  const handleSubscribe = () => {
    if (!clientRef.current || !topicToSubscribe) return;
    
    // Verificar si ya está suscrito
    if (subscribedTopics.includes(topicToSubscribe)) {
      addMessage('system', topicToSubscribe, '⚠️ Already subscribed to this topic');
      return;
    }
    
    clientRef.current.subscribe(topicToSubscribe, (err) => {
      if (!err) {
        setSubscribedTopics(prev => [...prev, topicToSubscribe]);
        addMessage('system', topicToSubscribe, '📥 SUBSCRIBED');
        setTopicToSubscribe('');
      } else {
        addMessage('error', topicToSubscribe, 'Failed to subscribe');
      }
    });
  };

  const handleUnsubscribe = (topic) => {
    if (!clientRef.current) return;
    
    clientRef.current.unsubscribe(topic, (err) => {
      if (!err) {
        setSubscribedTopics(prev => prev.filter(t => t !== topic));
        addMessage('system', topic, '📤 UNSUBSCRIBED');
      }
    });
  };

  const handlePublish = () => {
    if (!clientRef.current || !topicToPublish || !messageToPublish) return;
    
    // Validar y formatear JSON si es necesario
    let messageToSend = messageToPublish;
    if (showJsonFormatter) {
      try {
        const jsonObj = JSON.parse(messageToPublish);
        messageToSend = JSON.stringify(jsonObj);
      } catch (e) {
        addMessage('error', 'JSON Format', 'Invalid JSON format');
        return;
      }
    }
    
    clientRef.current.publish(topicToPublish, messageToSend, (err) => {
      if (!err) {
        addMessage('published', topicToPublish, messageToSend);
        setMessageToPublish('');
      } else {
        addMessage('error', topicToPublish, 'Failed to publish');
      }
    });
  };

  const clearMessages = () => {
    setMessages([]);
    // Limpiar también el localStorage
    localStorage.removeItem('mqttMessageLog');
    console.log('Cleared message log from localStorage');
  };

  const exportMessageLog = () => {
    if (messages.length === 0) {
      alert('No hay mensajes para exportar');
      return;
    }

    const exportData = {
      exportDate: new Date().toISOString(),
      totalMessages: messages.length,
      messages: messages.map(msg => ({
        timestamp: `${msg.date} ${msg.timestamp}`,
        topic: msg.topic,
        type: msg.type,
        payload: msg.payload,
        rawPayload: msg.rawPayload
      }))
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], {type: 'application/json'});
    
    const link = document.createElement('a');
    link.href = URL.createObjectURL(dataBlob);
    link.download = `mqtt-log-${new Date().toISOString().slice(0,19).replace(/:/g, '-')}.json`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log(`Exported ${messages.length} messages to JSON file`);
  };

  const clearSavedSubscriptions = () => {
    if (window.confirm('¿Eliminar todas las suscripciones guardadas?')) {
      localStorage.removeItem('mqttSubscriptions');
      // Desuscribirse de todos los topics actuales
      subscribedTopics.forEach(topic => handleUnsubscribe(topic));
      setSubscribedTopics([]);
      addMessage('system', 'Storage', '🗑️ Cleared all saved subscriptions');
    }
  };

  const exportMessages = () => {
    const dataStr = JSON.stringify(messages, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `mqtt_messages_${new Date().getTime()}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const getMessageStyle = (type) => {
    switch(type) {
      case 'received':
        return { background: '#e0f2fe', borderLeft: '4px solid #0284c7' };
      case 'published':
        return { background: '#dcfce7', borderLeft: '4px solid #16a34a' };
      case 'system':
        return { background: '#fef3c7', borderLeft: '4px solid #d97706' };
      case 'error':
        return { background: '#fee2e2', borderLeft: '4px solid #dc2626' };
      default:
        return { background: '#f3f4f6' };
    }
  };

  // Quick subscribe buttons mejorados
  const quickTopics = [
    { topic: 'devices/#', desc: 'Todos los dispositivos' },
    { topic: 'devices/+/status', desc: 'Estados' },
    { topic: 'devices/+/telemetry', desc: 'Telemetría' },
    { topic: 'devices/+/command', desc: 'Comandos' },
    { topic: 'system/#', desc: 'Sistema' }
  ];

  // Formato de JSON para mejor visualización
  const formatJsonPreview = (text) => {
    try {
      const obj = JSON.parse(text);
      return JSON.stringify(obj, null, 2);
    } catch {
      return text;
    }
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1>MQTT Console</h1>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ 
            display: 'inline-block',
            padding: '0.5rem 1rem',
            borderRadius: '8px',
            background: connected ? '#10b981' : connecting ? '#f59e0b' : '#ef4444',
            color: 'white',
            fontWeight: 'bold'
          }}>
            {connected ? '🟢 Connected' : connecting ? '🟡 Connecting...' : '🔴 Disconnected'}
          </div>
          
          <button
            onClick={exportMessages}
            style={{
              padding: '0.5rem 1rem',
              background: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            📥 Export Messages
          </button>
          
          <button
            onClick={clearSavedSubscriptions}
            style={{
              padding: '0.5rem 1rem',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            🗑️ Clear Saved Subs
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '2rem' }}>
        {/* Left Panel - Controls */}
        <div>
          {/* Subscribe Section */}
          <div style={{ background: 'white', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginTop: 0 }}>Subscribe to Topic</h3>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <input
                type="text"
                placeholder="e.g., devices/+/telemetry"
                value={topicToSubscribe}
                onChange={(e) => setTopicToSubscribe(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSubscribe()}
                disabled={!connected}
                style={{ 
                  flex: 1,
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px'
                }}
              />
              <button 
                onClick={handleSubscribe}
                disabled={!connected || !topicToSubscribe}
                style={{ 
                  padding: '0.5rem 1rem',
                  background: connected ? '#3b82f6' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: connected ? 'pointer' : 'not-allowed'
                }}
              >
                Subscribe
              </button>
            </div>
            
            {/* Quick Subscribe Buttons Mejorados */}
            <div style={{ marginBottom: '1rem' }}>
              <small style={{ color: '#6b7280' }}>Quick subscribe:</small>
              <div style={{ marginTop: '0.5rem' }}>
                {quickTopics.map(({ topic, desc }) => (
                  <button
                    key={topic}
                    onClick={() => {
                      setTopicToSubscribe(topic);
                      setTimeout(handleSubscribe, 100);
                    }}
                    disabled={!connected || subscribedTopics.includes(topic)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '0.5rem',
                      marginBottom: '0.25rem',
                      fontSize: '0.875rem',
                      background: subscribedTopics.includes(topic) ? '#e5e7eb' : '#f0f9ff',
                      border: '1px solid #bfdbfe',
                      borderRadius: '4px',
                      cursor: connected ? 'pointer' : 'not-allowed'
                    }}
                  >
                    <strong>{topic}</strong>
                    <span style={{ color: '#6b7280', marginLeft: '0.5rem', fontSize: '0.75rem' }}>
                      {desc}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Active Subscriptions con indicador de persistencia */}
            <div>
              <small style={{ color: '#6b7280' }}>
                Active subscriptions ({subscribedTopics.length}) 
                <span style={{ color: '#10b981', marginLeft: '0.5rem' }}>💾 Auto-saved</span>
              </small>
              <div style={{ marginTop: '0.5rem', maxHeight: '200px', overflowY: 'auto' }}>
                {subscribedTopics.length === 0 ? (
                  <div style={{ color: '#9ca3af', fontSize: '0.875rem', padding: '0.5rem' }}>
                    No active subscriptions
                  </div>
                ) : (
                  subscribedTopics.map(topic => (
                    <div key={topic} style={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.375rem 0.5rem',
                      marginBottom: '0.25rem',
                      background: '#f9fafb',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}>
                      <span style={{ fontFamily: 'monospace' }}>{topic}</span>
                      <button
                        onClick={() => handleUnsubscribe(topic)}
                        style={{
                          padding: '0.125rem 0.375rem',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >
                        ✖
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Publish Section con JSON formatter */}
          <div style={{ background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginTop: 0 }}>Publish Message</h3>
            <input
              type="text"
              placeholder="Topic (e.g., devices/arduino_001/command)"
              value={topicToPublish}
              onChange={(e) => setTopicToPublish(e.target.value)}
              disabled={!connected}
              style={{ 
                width: '100%',
                padding: '0.5rem',
                marginBottom: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px'
              }}
            />
            
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{ fontSize: '0.875rem' }}>
                <input
                  type="checkbox"
                  checked={showJsonFormatter}
                  onChange={(e) => setShowJsonFormatter(e.target.checked)}
                  style={{ marginRight: '0.5rem' }}
                />
                Format as JSON
              </label>
            </div>
            
            <textarea
              placeholder={showJsonFormatter ? '{"command": "on", "value": 100}' : 'Message (JSON or plain text)'}
              value={messageToPublish}
              onChange={(e) => setMessageToPublish(e.target.value)}
              disabled={!connected}
              rows="6"
              style={{ 
                width: '100%',
                padding: '0.5rem',
                marginBottom: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                background: showJsonFormatter ? '#f0f9ff' : 'white'
              }}
            />
            
            {showJsonFormatter && messageToPublish && (
              <div style={{ 
                marginBottom: '0.5rem',
                padding: '0.5rem',
                background: '#fef3c7',
                borderRadius: '4px',
                fontSize: '0.75rem'
              }}>
                <strong>Preview:</strong>
                <pre style={{ margin: '0.25rem 0', fontSize: '0.75rem' }}>
                  {formatJsonPreview(messageToPublish)}
                </pre>
              </div>
            )}
            
            <button 
              onClick={handlePublish}
              disabled={!connected || !topicToPublish || !messageToPublish}
              style={{ 
                width: '100%',
                padding: '0.5rem 1rem',
                background: connected ? '#10b981' : '#9ca3af',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: connected ? 'pointer' : 'not-allowed'
              }}
            >
              Publish
            </button>
          </div>
        </div>

        {/* Right Panel - Messages con mejoras */}
        <div style={{ background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0 }}>
              📜 Log de Mensajes ({messages.length}/100)
              {messages.length > 0 && (
                <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '1rem' }}>
                  Latest: {messages[0]?.date} {messages[0]?.timestamp}
                </span>
              )}
            </h3>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={exportMessageLog}
                disabled={messages.length === 0}
                style={{
                  padding: '0.25rem 0.75rem',
                  background: messages.length > 0 ? '#10b981' : '#f3f4f6',
                  color: messages.length > 0 ? 'white' : '#9ca3af',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  cursor: messages.length > 0 ? 'pointer' : 'not-allowed'
                }}
              >
                📥 Exportar
              </button>
              <button
                onClick={clearMessages}
                style={{
                  padding: '0.25rem 0.75rem',
                  background: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                🗑️ Limpiar
              </button>
            </div>
          </div>
          
          <div style={{ 
            height: '600px',
            overflowY: 'auto',
            border: '1px solid #e5e7eb',
            borderRadius: '4px',
            padding: '0.5rem'
          }}>
            {messages.length === 0 ? (
              <div style={{ 
                textAlign: 'center',
                color: '#9ca3af',
                padding: '2rem'
              }}>
                📜 Log vacío. Suscríbete a un tópico para recibir mensajes.
                <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>
                  Los últimos 100 mensajes se guardan automáticamente
                </div>
                {subscribedTopics.length > 0 && (
                  <div style={{ marginTop: '1rem', fontSize: '0.875rem' }}>
                    <strong>Listening on:</strong><br/>
                    {subscribedTopics.join(', ')}
                  </div>
                )}
              </div>
            ) : (
              messages.map(msg => (
                <div key={msg.id} style={{ 
                  ...getMessageStyle(msg.type),
                  padding: '0.75rem',
                  marginBottom: '0.5rem',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <strong style={{ color: '#1f2937' }}>{msg.topic}</strong>
                    <small style={{ color: '#6b7280' }}>{msg.timestamp}</small>
                  </div>
                  <div style={{ 
                    background: 'rgba(255,255,255,0.8)',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    wordBreak: 'break-all',
                    whiteSpace: 'pre-wrap',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    {msg.payload}
                  </div>
                  
                  {/* Análisis de datos para mensajes recibidos */}
                  {msg.type === 'received' && msg.rawPayload && (
                    <div style={{ 
                      marginTop: '0.5rem',
                      padding: '0.5rem',
                      background: 'rgba(59, 130, 246, 0.1)',
                      borderRadius: '4px',
                      fontSize: '0.75rem'
                    }}>
                      {(() => {
                        try {
                          const data = JSON.parse(msg.rawPayload);
                          const keys = Object.keys(data);
                          return (
                            <div>
                              <strong>📊 Data Analysis:</strong>
                              <div style={{ marginTop: '0.25rem' }}>
                                {keys.map(key => (
                                  <span key={key} style={{ 
                                    display: 'inline-block',
                                    margin: '0.125rem',
                                    padding: '0.125rem 0.375rem',
                                    background: '#3b82f6',
                                    color: 'white',
                                    borderRadius: '3px',
                                    fontSize: '0.7rem'
                                  }}>
                                    {key}: {typeof data[key] === 'object' ? 'object' : data[key]}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        } catch {
                          return null;
                        }
                      })()}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MqttConsole;